import { Link } from "react-router-dom";
import { Container, Row, Col, Button, Card, Accordion } from "react-bootstrap";
import {
  FiCheckCircle,
  FiCpu,
  FiGlobe,
  FiLayers,
  FiShield,
  FiTrendingUp,
  FiZap,
  FiHeadphones,
} from "react-icons/fi";
import heroImage from "../assets/homepage.png";
import investifyImage from "../assets/investify.png";
import webImage from "../assets/webdev.png";
import dashboardImage from "../assets/cta-bg.png";

const services = [
  {
    title: "Custom Software",
    description:
      "Tailored systems for operations, inventory, school, healthcare, and field teams.",
    icon: <FiLayers />,
  },
  {
    title: "Website Development",
    description:
      "High-converting websites that turn traffic into inquiries and sales.",
    icon: <FiGlobe />,
  },
  {
    title: "Mobile Apps",
    description:
      "Native and cross-platform apps that make your services more accessible.",
    icon: <FiCpu />,
  },
  {
    title: "AI Automation",
    description:
      "Automate support, lead handling, reporting, and repetitive work with AI.",
    icon: <FiZap />,
  },
];

const benefits = [
  {
    title: "Business first",
    description:
      "We focus on revenue, efficiency, and customer experience before writing code.",
    icon: <FiTrendingUp />,
  },
  {
    title: "Fast delivery",
    description:
      "Most projects begin within days, with a pragmatic plan and visible momentum.",
    icon: <FiShield />,
  },
  {
    title: "Long-term support",
    description:
      "We stay involved after launch to keep your tools reliable and useful.",
    icon: <FiHeadphones />,
  },
];

const industries = [
  "Real Estate",
  "Healthcare",
  "Construction",
  "Education",
  "Hospitality",
  "Retail",
  "Agriculture",
  "Financial Services",
];

const projects = [
  {
    title: "Investify",
    subtitle: "Real estate investment platform",
    image: investifyImage,
  },
  {
    title: "Inventory Control Suite",
    subtitle: "Operations and stock visibility dashboard",
    image: webImage,
  },
  {
    title: "Clinic Operations Hub",
    subtitle: "Patient and appointment management system",
    image: dashboardImage,
  },
];

const faqs = [
  {
    question: "How long does a project take?",
    answer:
      "It depends on scope, but we usually move from discovery to prototype within days and launch quickly with clear milestones.",
  },
  {
    question: "Do you build custom software?",
    answer:
      "Yes. We design custom solutions around your business process rather than forcing you into a generic platform.",
  },
  {
    question: "Can you redesign our current website?",
    answer:
      "Absolutely. We can improve your messaging, conversion flow, speed, and mobile experience in a focused redesign.",
  },
];

function Home() {
  return (
    <div>
      <section className="hero-shell">
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={7}>
              <div className="hero-badge">
                <FiCheckCircle /> Business growth software for ambitious teams
              </div>
              <h1 className="hero-title">
                Build software that helps your business grow.
              </h1>
              <p className="hero-copy mt-3">
                We help Nigerian businesses increase revenue, automate
                operations, and improve customer experience through custom
                software, AI automation, and modern websites.
              </p>
              <div className="hero-cta-group">
                <Button
                  as={Link}
                  to="/contact"
                  size="lg"
                  className="rounded-pill px-4"
                >
                  Book a Free Consultation
                </Button>
                <Button
                  as={Link}
                  to="/portfolio"
                  variant="outline-primary"
                  size="lg"
                  className="rounded-pill px-4"
                >
                  View Our Work
                </Button>
              </div>
              <div className="hero-stats">
                <div className="hero-stat-card">
                  <strong>24/7</strong>
                  <div className="small text-muted">Automation support</div>
                </div>
                <div className="hero-stat-card">
                  <strong>Fast</strong>
                  <div className="small text-muted">Project kickoff</div>
                </div>
                <div className="hero-stat-card">
                  <strong>Custom</strong>
                  <div className="small text-muted">Business-first systems</div>
                </div>
              </div>
            </Col>
            <Col lg={5}>
              <div className="hero-visual">
                <div className="hero-card">
                  <img src={heroImage} alt="CodeCamp dashboard preview" />
                </div>
                <div className="hero-floating-card">
                  <div className="small text-uppercase fw-semibold opacity-75">
                    Trusted by growth-minded teams
                  </div>
                  <div className="fw-bold mt-2">
                    From websites to AI automation and operations systems.
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="section-shell accent-bg">
        <Container>
          <div className="text-center mb-4">
            <h2 className="section-title">
              Digital tools that make businesses more profitable
            </h2>
            <p className="section-lead mx-auto">
              We solve real bottlenecks in sales, service, operations, and
              communication with technology designed around how your business
              actually works.
            </p>
          </div>
          <Row className="g-4 mt-2">
            {services.map((service) => (
              <Col md={6} lg={3} key={service.title}>
                <Card className="service-card h-100">
                  <div className="service-icon">{service.icon}</div>
                  <h5 className="fw-bold">{service.title}</h5>
                  <p className="text-muted mb-0">{service.description}</p>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="section-shell">
        <Container>
          <Row className="g-4 align-items-center">
            <Col lg={6}>
              <div className="news-card">
                <div className="small text-uppercase fw-semibold opacity-75">
                  Why CodeCamp
                </div>
                <h3 className="mt-2 mb-3">
                  We turn business challenges into reliable digital systems.
                </h3>
                <p className="mb-0 text-light opacity-80">
                  Whether you need more customers, smoother operations, or
                  smarter automation, we build practical solutions that create
                  measurable value.
                </p>
              </div>
            </Col>
            <Col lg={6}>
              <Row className="g-3">
                {benefits.map((benefit) => (
                  <Col xs={12} key={benefit.title}>
                    <div className="benefit-card p-3">
                      <div className="service-icon mb-3">{benefit.icon}</div>
                      <h5 className="fw-bold">{benefit.title}</h5>
                      <p className="text-muted mb-0">{benefit.description}</p>
                    </div>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="section-shell accent-bg">
        <Container>
          <div className="d-flex justify-content-between align-items-end flex-wrap gap-3 mb-4">
            <div>
              <h2 className="section-title">Featured work</h2>
              <p className="section-lead mb-0">
                Selected projects that show how we combine strategy, design, and
                engineering.
              </p>
            </div>
            <Button
              as={Link}
              to="/portfolio"
              variant="outline-primary"
              className="rounded-pill"
            >
              See more projects
            </Button>
          </div>
          <Row className="g-4">
            {projects.map((project) => (
              <Col md={4} key={project.title}>
                <Card className="project-card overflow-hidden">
                  <Card.Img
                    variant="top"
                    src={project.image}
                    height="220"
                    style={{ objectFit: "cover" }}
                  />
                  <Card.Body>
                    <Card.Title>{project.title}</Card.Title>
                    <Card.Text className="text-muted">
                      {project.subtitle}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="section-shell">
        <Container>
          <div className="text-center mb-4">
            <h2 className="section-title">Industries we support</h2>
            <p className="section-lead mx-auto">
              We tailor every solution to the realities of the sector you
              operate in.
            </p>
          </div>
          <Row className="g-3">
            {industries.map((industry) => (
              <Col md={6} lg={3} key={industry}>
                <div className="industry-card p-3 h-100">
                  <h6 className="fw-bold mb-2">{industry}</h6>
                  <p className="text-muted mb-0">
                    Practical digital solutions that simplify daily operations
                    and improve customer experience.
                  </p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="section-shell accent-bg">
        <Container>
          <Row className="g-4 align-items-center">
            <Col lg={6}>
              <h2 className="section-title">
                AI is changing business. We help you use it well.
              </h2>
              <p className="section-lead">
                From customer support to lead qualification and reporting, our
                AI solutions are designed to save time and create a better
                experience for your team and customers.
              </p>
              <ul className="list-unstyled">
                <li className="mb-2">
                  <FiCheckCircle className="me-2 text-primary" />
                  Answer customer questions 24/7
                </li>
                <li className="mb-2">
                  <FiCheckCircle className="me-2 text-primary" />
                  Qualify leads automatically
                </li>
                <li className="mb-2">
                  <FiCheckCircle className="me-2 text-primary" />
                  Automate workflows and reporting
                </li>
              </ul>
            </Col>
            <Col lg={6}>
              <div className="page-card">
                <img
                  src={dashboardImage}
                  alt="AI dashboard preview"
                  className="rounded-4"
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="section-shell">
        <Container>
          <Row className="g-4">
            <Col lg={6}>
              <h2 className="section-title">Frequently asked questions</h2>
              <p className="section-lead">
                A few answers to help you decide if CodeCamp is the right fit.
              </p>
            </Col>
            <Col lg={6}>
              <Accordion defaultActiveKey="0">
                {faqs.map((faq, index) => (
                  <Accordion.Item eventKey={String(index)} key={faq.question}>
                    <Accordion.Header>{faq.question}</Accordion.Header>
                    <Accordion.Body>{faq.answer}</Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="section-shell">
        <Container>
          <div className="news-card text-center">
            <h2 className="mb-3">Ready to build something great?</h2>
            <p className="text-light opacity-80 mb-4">
              Let’s discuss your project and map the right digital solution for
              your business.
            </p>
            <Button
              as={Link}
              to="/contact"
              size="lg"
              variant="light"
              className="rounded-pill px-4"
            >
              Schedule a Free Consultation
            </Button>
          </div>
        </Container>
      </section>
    </div>
  );
}

export default Home;
